/**
 * WorkforceStaffing — per-event staffing: which roles each event needs, who
 * fills them, and readiness at a glance. List + detail (route
 * workforce.staffing.event?id=…).
 *
 * Ops: listEventStaffing / getEventStaffing / suggestEventStaffing /
 * updateEventStaffing / assignEventRole — every mutation awaited (mandate).
 * Role plans are auto-suggested server-side from capacity/registrations and
 * stay fully editable here (operator decision 2026-07-16: "both").
 */
import { useEffect, useMemo, useState } from "react";
import { Btn, Icon, Input, Pill, T, alpha } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { StatTile, FilterBar, EmptyState, FilterDropdown } from "../../shell/composites";
import { thStyle, tdStyle } from "../events/EventCard";
import { NumberField } from "../../lib/NumberField";
import { can } from "../../shell/access";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { useStaff } from "../staff/useStaff";
import { useWfResource, useBusy } from "./useWorkforce";
import {
  listEventStaffing, getEventStaffing, suggestEventStaffing, updateEventStaffing, assignEventRole,
  type EventStaffing, type EventStaffingRole,
} from "./wf-api";
import {
  LocationFilter, WfPending, readinessPill, TinyProgress, roleHue,
  isoToday, addDaysIso, dayLabel, hmRangeLabel, hmLabel, halfHourOptions, labelToHm,
} from "./wf-shared";
import { useT, t as i18nT } from "../../lib/i18n";

const filled = (r: EventStaffingRole) => r.assignees.length;
const rolesFilled = (ev: EventStaffing) => ev.roles.reduce((s, r) => s + Math.min(filled(r), r.count), 0);
const rolesNeeded = (ev: EventStaffing) => ev.roles.reduce((s, r) => s + r.count, 0);

export default function WorkforceStaffing() {
  const t = useT();
  const nav = useNav();
  const [locationId, setLocationId] = useState<string | null>(null);
  const [readiness, setReadiness] = useState<"all" | "ready" | "partial" | "unstaffed">("all");
  const from = isoToday();
  const to = addDaysIso(from, 30);
  const { data, loading, error, reload } = useWfResource<{ items: EventStaffing[] }>(
    `staffing:${locationId ?? "all"}`, () => listEventStaffing(from, to, locationId ?? undefined));

  const items = data?.items ?? [];
  const rows = items.filter((e) => readiness === "all" || e.readiness === readiness);
  const openRoles = items.reduce((s, e) => s + Math.max(0, rolesNeeded(e) - rolesFilled(e)), 0);

  const chip = (id: typeof readiness, label: string) => (
    <button key={id} data-testid="wfstaffing-readiness-chip" data-id={id} onClick={() => setReadiness(id)}
      style={{ padding: "6px 12px", borderRadius: 999, border: `1px solid ${readiness === id ? T.p1 : T.border}`, background: readiness === id ? T.p1 : T.white, color: readiness === id ? T.white : T.n2, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>{label}</button>
  );

  return (
    <Screen rail="wf-staffing">
      <PageHeader title={t("workforce.staffing.eventStaffing", "Event staffing")} subtitle={t("workforce.staffing.rolesEachUpcomingEventNeedsAndWhoSCoveringThemAutoSuggested", "Roles each upcoming event needs and who's covering them — auto-suggested from capacity and registrations, editable per event.")} breadcrumb={null} />
      <DataSourceStrip screen="wfstaffing" ops={["listEventStaffing"]} />
      <div style={{ padding: "16px 24px 0", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <StatTile label={t("workforce.staffing.eventsNext30d", "Events (next 30d)")} value={items.length} icon={<Icon.ticket size={14} />} accent={T.p1} />
        <StatTile label={t("workforce.staffing.fullyStaffed", "Fully staffed")} value={items.filter((e) => e.readiness === "ready").length} icon={<Icon.check size={14} />} accent={T.green1} />
        <StatTile label={t("workforce.staffing.needsAttention", "Needs attention")} value={items.filter((e) => e.readiness !== "ready").length} icon={<Icon.warn size={14} />} accent={T.orange1} />
        <StatTile label={t("workforce.staffing.openRoleSlots", "Open role slots")} value={openRoles} icon={<Icon.users size={14} />} accent={T.tDarkBlue} />
      </div>
      <FilterBar>
        <LocationFilter value={locationId} onChange={setLocationId} />
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        {chip("all", `All · ${items.length}`)}
        {chip("unstaffed", `Unstaffed · ${items.filter((e) => e.readiness === "unstaffed").length}`)}
        {chip("partial", `Partial · ${items.filter((e) => e.readiness === "partial").length}`)}
        {chip("ready", `Staffed · ${items.filter((e) => e.readiness === "ready").length}`)}
        <span style={{ marginLeft: "auto", fontSize: 12, color: T.soft }}>{t("workforce.staffing.next30Days", "Next 30 days")}</span>
      </FilterBar>
      <div style={{ flex: 1, overflow: "auto", padding: "0 24px 24px" }}>
        {error ? <WfPending screen="wfstaffing" opName="listEventStaffing" error={error} onRetry={reload} />
          : loading ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("workforce.staffing.loadingEventStaffing", "Loading event staffing…")}</div>
          : rows.length === 0 ? (
            <EmptyState icon={<Icon.ticket size={20} />} title={t("workforce.staffing.noUpcomingEventsInRange", "No upcoming events in range")} body={t("workforce.staffing.eventsPublishWithAnAutoSuggestedStaffingPlanTheyAppearHereFor", "Events publish with an auto-suggested staffing plan; they appear here for the next 30 days.")} />
          ) : (
            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.shadowSm, overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead><tr>
                  <th style={thStyle}>{t("workforce.staffing.event", "Event")}</th><th style={thStyle}>{t("workforce.staffing.when", "When")}</th><th style={thStyle}>{t("workforce.staffing.location", "Location")}</th>
                  <th style={thStyle}>{t("workforce.staffing.registered", "Registered")}</th><th style={thStyle}>{t("workforce.staffing.rolesFilled", "Roles filled")}</th><th style={thStyle}>{t("workforce.staffing.readiness", "Readiness")}</th>
                </tr></thead>
                <tbody>
                  {rows.map((e) => (
                    <tr key={e.eventId} data-testid="wfstaffing-event-row" data-id={e.eventId} onClick={() => nav.go("workforce.staffing.event", { id: e.eventId })}
                      style={{ cursor: "pointer" }}
                      onMouseEnter={(ev) => (ev.currentTarget.style.background = T.canvas)} onMouseLeave={(ev) => (ev.currentTarget.style.background = "transparent")}>
                      <td style={tdStyle}>
                        <span style={{ fontWeight: 600, color: T.n1, display: "inline-flex", alignItems: "center", gap: 8 }}>
                          {e.title}
                          {e.suggested ? <span title={t("workforce.staffing.autoSuggestedPlanNotYetReviewed", "Auto-suggested plan — not yet reviewed")} style={{ fontSize: 10, fontWeight: 700, color: T.p1, background: T.p4, borderRadius: 999, padding: "1px 7px" }}>{t("workforce.staffing.suggested", "SUGGESTED")}</span> : null}
                        </span>
                      </td>
                      <td style={tdStyle}>{dayLabel(e.date, "full")} · {hmRangeLabel(e.start, e.end)}</td>
                      <td style={tdStyle}>{e.locationName}</td>
                      <td style={tdStyle}><span style={{ fontFamily: T.fontMono }}>{e.registered}{e.capacity ? `/${e.capacity}` : ""}</span></td>
                      <td style={tdStyle}><TinyProgress num={rolesFilled(e)} den={rolesNeeded(e)} /></td>
                      <td style={tdStyle}>{readinessPill(e.readiness)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </Screen>
  );
}

/* ─── Detail — one event's staffing plan ──────────────────────────────── */
export function EventStaffingDetail() {
  const t = useT();
  const nav = useNav();
  const eventId = String(nav.params.id ?? "");
  const { data, loading, error, reload } = useWfResource<EventStaffing>(`staffing-ev:${eventId}`, () => getEventStaffing(eventId));
  const { members } = useStaff();
  const canEdit = can("staffing.write");
  const { busy, run } = useBusy();
  const [roles, setRoles] = useState<EventStaffingRole[] | null>(null); // ui-local edit buffer; persisted via Save (updateEventStaffing)
  useEffect(() => { if (data) setRoles(data.roles.map((r) => ({ ...r, assignees: [...r.assignees] }))); }, [data]);
  const dirty = useMemo(() => !!data && !!roles && JSON.stringify(roles) !== JSON.stringify(data.roles), [data, roles]);
  const times = halfHourOptions(6, 23);

  const patchRole = (id: string, patch: Partial<EventStaffingRole>) => // ui-local
    setRoles((rs) => (rs ?? []).map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const addRole = () => setRoles((rs) => ([...(rs ?? []), { id: `new-${Date.now().toString(36)}`, label: "", count: 1, skill: null, window: { start: data?.start ?? "09:00", end: data?.end ?? "17:00" }, assignees: [] }])); // ui-local until Save
  const removeRole = (id: string) => setRoles((rs) => (rs ?? []).filter((r) => r.id !== id)); // ui-local until Save

  const save = async () => {
    if (!roles) return;
    await run("save", async () => {
      try {
        await updateEventStaffing(eventId, roles);
        toast({ tone: "success", title: t("workforce.staffing.staffingPlanSaved", "Staffing plan saved") });
        reload();
      } catch (e) { toast({ tone: "danger", title: t("workforce.staffing.couldnTSaveThePlan", "Couldn't save the plan"), subtitle: e instanceof Error ? e.message : String(e) }); }
    });
  };
  const suggest = async () => {
    await run("suggest", async () => {
      try {
        await suggestEventStaffing(eventId);
        toast({ tone: "success", title: t("workforce.staffing.suggestedPlanGenerated", "Suggested plan generated"), subtitle: t("workforce.staffing.derivedFromCapacityRegistrationsAndDurationEditFreely", "Derived from capacity, registrations and duration — edit freely.") });
        reload();
      } catch (e) { toast({ tone: "danger", title: t("workforce.staffing.couldnTGenerateASuggestion", "Couldn't generate a suggestion"), subtitle: e instanceof Error ? e.message : String(e) }); }
    });
  };
  const assign = async (role: EventStaffingRole, staffName: string) => {
    const m = members.find((x) => (x.name || `${x.firstName} ${x.lastName}`.trim()) === staffName);
    if (!m) return;
    await run(`assign:${role.id}`, async () => {
      try {
        await assignEventRole(eventId, role.id, m.id);
        toast({ tone: "success", title: i18nT("workforce.staffing.staffnameAssigned.icu", { value0: staffName }), subtitle: i18nT("workforce.staffing.roleLabelRoleALinkedShiftWasCreatedOnTheSchedule.icu", { value0: role.label || "Role" }) });
        reload();
      } catch (e) { toast({ tone: "danger", title: t("workforce.staffing.couldnTAssign", "Couldn't assign"), subtitle: e instanceof Error ? e.message : String(e) }); }
    });
  };

  return (
    <Screen rail="wf-staffing">
      <PageHeader
        title={data ? data.title : t("chrome.searchPanel.eventStaffing", "Event staffing")}
        subtitle={data ? t("workforce.workforceStaffing.daylabelDataDateFullHmrangelabelDataStartDataEndDataLocationnameData", { value0: dayLabel(data.date, "full"), value1: hmRangeLabel(data.start, data.end), value2: data.locationName, value3: data.registered, value4: data.capacity ? `/${data.capacity}` : "" }) : undefined}
        breadcrumb={<span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("workforce.staffing")}><Icon.arrow dir="left" size={11} /> {t("workforce.staffing.eventStaffing", "Event staffing")}</span>}
        actions={<>
          {data ? readinessPill(data.readiness) : null}
          {canEdit ? <Btn variant="secondary" icon={<Icon.refresh size={14} />} testid="wfstaffingev-suggest-action" disabled={busy !== null} onClick={suggest}>{busy === "suggest" ? t("workforce.workforceStaffing.suggesting", "Suggesting…") : t("workforce.staffing.autoSuggest", "Auto-suggest")}</Btn> : null}
          {canEdit ? <Btn icon={<Icon.check size={14} />} testid="wfstaffingev-save-action" disabled={busy !== null || !dirty} onClick={save}>{busy === "save" ? t("workforce.workforceStaffing.saving", "Saving…") : t("workforce.staffing.savePlan", "Save plan")}</Btn> : null}
        </>}
      />
      <DataSourceStrip screen="wfstaffingev" ops={["getEventStaffing", "suggestEventStaffing", "updateEventStaffing", "assignEventRole"]} />
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 24px" }}>
        {error ? <WfPending screen="wfstaffingev" opName="getEventStaffing" error={error} onRetry={reload} />
          : loading || !roles ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("workforce.staffing.loadingPlan", "Loading plan…")}</div>
          : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 980 }}>
              {data?.suggested ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 8, background: T.p4, border: `1px solid ${T.p2}`, fontSize: 12.5, color: T.n1 }}>
                  <Icon.help size={14} />
                  {t("workforce.staffing.thisPlanWasAutoSuggestedFromTheEventSCapacityAndRegistrations", "This plan was auto-suggested from the event's capacity and registrations and hasn't been reviewed. Edit anything, then")}{" "}<b>{t("workforce.staffing.savePlan", "Save plan")}</b>.
                </div>
              ) : null}
              {roles.length === 0 ? (
                <EmptyState icon={<Icon.users size={20} />} title={t("workforce.staffing.noRolesYet", "No roles yet")} body={t("workforce.staffing.addTheRolesThisEventNeedsOrGenerateASuggestedPlan", "Add the roles this event needs, or generate a suggested plan.")}
                  action={canEdit ? <Btn variant="secondary" icon={<Icon.refresh size={14} />} onClick={suggest}>{t("workforce.staffing.autoSuggest", "Auto-suggest")}</Btn> : null} />
              ) : roles.map((r) => {
                const hue = roleHue(r.label || "Role");
                const short = filled(r) < r.count;
                return (
                  <div key={r.id} data-testid="wfstaffingev-role-row" data-id={r.id} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.shadowSm, padding: "14px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
                      <span aria-hidden style={{ width: 10, height: 34, borderRadius: 5, background: hue, alignSelf: "center", flexShrink: 0 }} />
                      <div style={{ flex: "2 1 200px", minWidth: 160 }}>
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: T.soft, display: "block", marginBottom: 4 }}>{t("workforce.staffing.role", "Role")}</span>
                        <Input value={r.label} placeholder={t("workforce.staffing.eGCheckInHostFloater", "e.g. Check-in, Host, Floater")} disabled={!canEdit} onChange={(e) => patchRole(r.id, { label: e.target.value })} testid="wfstaffingev-role-label-input" />
                      </div>
                      <div style={{ width: 90 }}>
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: T.soft, display: "block", marginBottom: 4 }}>{t("workforce.staffing.heads", "Heads")}</span>
                        <NumberField value={r.count} min={1} disabled={!canEdit} ariaLabel={`${r.label || "Role"} headcount`} onChange={(n) => patchRole(r.id, { count: Math.max(1, n) })} />
                      </div>
                      <div>
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: T.soft, display: "block", marginBottom: 4 }}>{t("workforce.staffing.from", "From")}</span>
                        <FilterDropdown label={hmLabel(r.window.start)} active options={times} onPick={(t) => patchRole(r.id, { window: { ...r.window, start: labelToHm(t) } })} onClear={() => undefined} />
                      </div>
                      <div>
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: T.soft, display: "block", marginBottom: 4 }}>{t("workforce.staffing.to", "To")}</span>
                        <FilterDropdown label={hmLabel(r.window.end)} active options={times} onPick={(t) => patchRole(r.id, { window: { ...r.window, end: labelToHm(t) } })} onClear={() => undefined} />
                      </div>
                      <span style={{ marginLeft: "auto", alignSelf: "center" }}><TinyProgress num={filled(r)} den={r.count} /></span>
                      {canEdit ? (
                        <button data-testid="wfstaffingev-role-remove" aria-label={t("workforce.staffing.removeRole", "Remove role")} onClick={() => removeRole(r.id)}
                          style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${T.border}`, background: T.white, color: T.red1, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", alignSelf: "center" }}><Icon.trash size={14} /></button>
                      ) : null}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      {r.assignees.map((a) => (
                        <span key={a.shiftId} data-testid="wfstaffingev-assignee-chip" data-id={a.staffId} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 11px", borderRadius: 999, background: alpha(hue, 12), border: `1px solid ${alpha(hue, 35)}`, fontSize: 12.5, fontWeight: 600, color: T.n1 }}>
                          <Icon.user size={12} /> {a.staffName}
                        </span>
                      ))}
                      {short && canEdit ? (
                        <FilterDropdown
                          label={i18nT("workforce.staffing.assignStaffRCountFilledRNeeded.icu", { value0: r.count - filled(r) })}
                          active={false}
                          options={members.map((m) => m.name || `${m.firstName} ${m.lastName}`.trim()).filter(Boolean)}
                          onPick={(name) => { void assign(r, name); }}
                          onClear={() => undefined}
                        />
                      ) : null}
                      {short && !canEdit ? <Pill tone="warn" dot>{r.count - filled(r)} {t("workforce.staffing.unfilled", "unfilled")}</Pill> : null}
                      {busy === `assign:${r.id}` ? <span style={{ fontSize: 12, color: T.soft }}>{t("workforce.staffing.assigning", "Assigning…")}</span> : null}
                    </div>
                  </div>
                );
              })}
              {canEdit ? (
                <button data-testid="wfstaffingev-role-add" onClick={addRole}
                  style={{ border: `1.5px dashed ${T.n4}`, background: "transparent", borderRadius: 8, padding: "13px 16px", fontSize: 13, fontWeight: 600, color: T.n2, cursor: "pointer", fontFamily: T.font, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <Icon.plus size={14} /> {t("workforce.staffing.addARole", "Add a role")}</button>
              ) : null}
              <span style={{ fontSize: 12, color: T.soft }}>{t("workforce.staffing.assigningStaffCreatesALinkedShiftOnTheScheduleForTheRole", "Assigning staff creates a linked shift on the schedule for the role's window. Event demand in the forecast comes from this plan.")}</span>
            </div>
          )}
      </div>
    </Screen>
  );
}
